import React, { useState, useEffect, useCallback } from 'react';
import './SuperAdminPage.css';

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}
function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}
function fmtTokens(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

// ── Icons ──────────────────────────────────────────────────────────────────────
function BuildingIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <rect x="2" y="4" width="16" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 8h2M6 11h2M12 8h2M12 11h2M8 18v-4h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function ChartIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <path d="M3 15l4-5 4 3 4-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 17h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function ChevronIcon({ open }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
      <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function HomeIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" width={size} height={size}>
      <path d="M3 9.5L10 3l7 6.5V17a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M7 18v-6h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}
function BookIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" width={size} height={size}>
      <path d="M4 4h12v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4 8h12M8 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Charts ─────────────────────────────────────────────────────────────────────

function SessionsTrendChart({ data }) {
  if (!data || data.length === 0) return <div className="sa-chart-empty">No data</div>;
  const maxSess = Math.max(...data.map(d => d.sessions), 1);
  const W = 560, H = 140, PL = 32, PR = 8, PT = 10, PB = 28;
  const cW = W - PL - PR, cH = H - PT - PB;
  const xs = data.map((_, i) => PL + (i / (data.length - 1)) * cW);
  const ys = data.map(d => PT + cH - (d.sessions / maxSess) * cH);
  const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  const area = `${path} L${xs[xs.length-1]},${PT+cH} L${xs[0]},${PT+cH} Z`;
  const ticks = [0, 3, 6, 9, 13];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="sa-svg-chart">
      <defs>
        <linearGradient id="sa-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C8102E" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="#C8102E" stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f} x1={PL} x2={W-PR} y1={PT + cH*(1-f)} y2={PT + cH*(1-f)}
          stroke="#e8ecf4" strokeWidth="1"/>
      ))}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <text key={f} x={PL-4} y={PT + cH*(1-f) + 4} textAnchor="end"
          fontSize="9" fill="#9aaabf">{Math.round(maxSess*f)}</text>
      ))}
      <path d={area} fill="url(#sa-grad)"/>
      <path d={path} fill="none" stroke="#C8102E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r="3" fill="#C8102E" stroke="#fff" strokeWidth="1.5">
          <title>{data[i].date}: {data[i].sessions} sessions</title>
        </circle>
      ))}
      {ticks.map(i => (
        <text key={i} x={xs[i]} y={H-6} textAnchor="middle" fontSize="9" fill="#9aaabf">
          {data[i].date}
        </text>
      ))}
    </svg>
  );
}

function TokensByOrgChart({ data }) {
  if (!data || data.length === 0) return <div className="sa-chart-empty">No data</div>;
  const maxT = Math.max(...data.map(d => d.total_tokens), 1);
  const BAR_H = 28, GAP = 10, LABEL_W = 110, NUM_W = 60, PAD = 12;
  const H = data.length * (BAR_H + GAP) + PAD * 2;
  const W = 420;
  const barW = W - LABEL_W - NUM_W - PAD;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="sa-svg-chart">
      {data.map((d, i) => {
        const y = PAD + i * (BAR_H + GAP);
        const iW = Math.max((d.input_tokens / maxT) * barW, 0);
        const oW = Math.max((d.output_tokens / maxT) * barW, 0);
        const label = d.org.length > 14 ? d.org.slice(0, 13) + '…' : d.org;
        return (
          <g key={d.org}>
            <text x={LABEL_W - 6} y={y + BAR_H / 2 + 4} textAnchor="end"
              fontSize="11" fill="#3a4d6e">{label}</text>
            <rect x={LABEL_W} y={y} width={iW} height={BAR_H * 0.55}
              rx="3" fill="#1A2744" opacity="0.85">
              <title>{d.org}: {fmtTokens(d.input_tokens)} input tokens</title>
            </rect>
            <rect x={LABEL_W} y={y + BAR_H * 0.55 + 2} width={oW} height={BAR_H * 0.4}
              rx="3" fill="#C8102E" opacity="0.75">
              <title>{d.org}: {fmtTokens(d.output_tokens)} output tokens</title>
            </rect>
            <text x={LABEL_W + Math.max(iW, oW) + 6} y={y + BAR_H / 2 + 4}
              fontSize="10" fill="#6a7d9a">{fmtTokens(d.total_tokens)}</text>
          </g>
        );
      })}
      <rect x={LABEL_W} y={H - 18} width={10} height={8} rx="2" fill="#1A2744" opacity="0.85"/>
      <text x={LABEL_W + 14} y={H - 11} fontSize="9" fill="#9aaabf">Input</text>
      <rect x={LABEL_W + 60} y={H - 18} width={10} height={8} rx="2" fill="#C8102E" opacity="0.75"/>
      <text x={LABEL_W + 74} y={H - 11} fontSize="9" fill="#9aaabf">Output</text>
    </svg>
  );
}

// ── Org Details Panel ─────────────────────────────────────────────────────────

function OrgDetailsPanel({ orgId, orgName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('properties');

  useEffect(() => {
    fetch(`${API_BASE}/api/organizations/${orgId}/details`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [orgId]);

  if (loading) return <div className="sa-detail-loading">Loading details…</div>;
  if (!data)   return <div className="sa-detail-loading">Failed to load.</div>;

  return (
    <div className="sa-detail-panel">
      <div className="sa-detail-tabs">
        <button className={`sa-tab ${tab === 'properties' ? 'sa-tab--active' : ''}`}
          onClick={() => setTab('properties')}>
          <HomeIcon /> Properties ({data.properties.length})
        </button>
        <button className={`sa-tab ${tab === 'guidebooks' ? 'sa-tab--active' : ''}`}
          onClick={() => setTab('guidebooks')}>
          <BookIcon /> Guidebooks ({data.guidebooks.length})
        </button>
      </div>

      {tab === 'properties' && (
        data.properties.length === 0
          ? <div className="sa-detail-empty">No properties in this organization.</div>
          : <table className="sa-table">
              <thead>
                <tr><th>Property Address</th><th>Manager</th><th>Guidebooks</th></tr>
              </thead>
              <tbody>
                {data.properties.map(p => (
                  <tr key={p.propId}>
                    <td>{p.property_address}</td>
                    <td>{p.manager_name || <span className="sa-muted">—</span>}</td>
                    <td>{p.guidebook_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
      )}

      {tab === 'guidebooks' && (
        data.guidebooks.length === 0
          ? <div className="sa-detail-empty">No guidebooks in this organization.</div>
          : <table className="sa-table">
              <thead>
                <tr><th>Guidebook</th><th>Properties</th><th>Sessions</th><th>Input Tokens</th><th>Output Tokens</th><th>Total</th></tr>
              </thead>
              <tbody>
                {data.guidebooks.map(g => (
                  <tr key={g.guideid}>
                    <td>{g.guidebook_title}</td>
                    <td>{g.property_count}</td>
                    <td>{g.session_count}</td>
                    <td>{fmtTokens(g.input_tokens)}</td>
                    <td>{fmtTokens(g.output_tokens)}</td>
                    <td className="sa-bold">{fmtTokens(g.total_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
      )}
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function OrgBadge({ name }) {
  if (!name) return <span className="sa-muted">—</span>;
  return <span className="sa-org-badge">{name}</span>;
}

function useListData(url) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}${url}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [url]);
  return { data, loading, search, setSearch };
}

// ── All Guidebooks View ───────────────────────────────────────────────────────

function GuidebooksView() {
  const { data, loading, search, setSearch } = useListData('/api/organizations/all-guidebooks');

  const filtered = (data || []).filter(g =>
    g.guidebook_title.toLowerCase().includes(search.toLowerCase()) ||
    (g.org_name || '').toLowerCase().includes(search.toLowerCase())
  );

  // group by org for the count badge in search bar
  const orgCount = new Set((data || []).map(g => g.org_name)).size;

  return (
    <>
      <div className="sa-topbar">
        <div>
          <h1 className="sa-page-title">All Guidebooks</h1>
          <p className="sa-page-sub">
            {loading ? 'Loading…' : `${(data||[]).length} guidebooks across ${orgCount} organizations`}
          </p>
        </div>
      </div>

      <div className="sa-search-row">
        <input className="sa-search" placeholder="Search by title or organization…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="sa-loading">Loading guidebooks…</div>
      ) : filtered.length === 0 ? (
        <div className="sa-empty">{search ? 'No guidebooks match your search.' : 'No guidebooks found.'}</div>
      ) : (
        <div className="sa-list-panel">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Guidebook</th>
                <th>Organization</th>
                <th>Properties</th>
                <th>Sessions</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
                <th>Total Tokens</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => (
                <tr key={g.guideid}>
                  <td>
                    <span className="sa-item-title">{g.guidebook_title}</span>
                    {g.guide_chatbot_url && (
                      <span className="sa-item-url">{g.guide_chatbot_url}</span>
                    )}
                  </td>
                  <td><OrgBadge name={g.org_name} /></td>
                  <td>{g.property_count}</td>
                  <td>{g.session_count}</td>
                  <td>{fmtTokens(g.input_tokens)}</td>
                  <td>{fmtTokens(g.output_tokens)}</td>
                  <td className="sa-bold">{fmtTokens(g.total_tokens)}</td>
                  <td className="sa-muted-sm">{g.created_date ? g.created_date.slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── All Properties View ───────────────────────────────────────────────────────

function PropertiesView() {
  const { data, loading, search, setSearch } = useListData('/api/organizations/all-properties');

  const filtered = (data || []).filter(p =>
    p.property_address.toLowerCase().includes(search.toLowerCase()) ||
    (p.org_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.manager_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const orgCount = new Set((data || []).map(p => p.org_name)).size;

  return (
    <>
      <div className="sa-topbar">
        <div>
          <h1 className="sa-page-title">All Properties</h1>
          <p className="sa-page-sub">
            {loading ? 'Loading…' : `${(data||[]).length} properties across ${orgCount} organizations`}
          </p>
        </div>
      </div>

      <div className="sa-search-row">
        <input className="sa-search" placeholder="Search by address, org or manager…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="sa-loading">Loading properties…</div>
      ) : filtered.length === 0 ? (
        <div className="sa-empty">{search ? 'No properties match your search.' : 'No properties found.'}</div>
      ) : (
        <div className="sa-list-panel">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Property Address</th>
                <th>Organization</th>
                <th>Manager</th>
                <th>Guidebooks</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.propId}>
                  <td className="sa-item-title">{p.property_address}</td>
                  <td><OrgBadge name={p.org_name} /></td>
                  <td>{p.manager_name || <span className="sa-muted">Unassigned</span>}</td>
                  <td>{p.guidebook_count}</td>
                  <td className="sa-muted-sm">{p.created_date ? p.created_date.slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Create-Org Modal ──────────────────────────────────────────────────────────

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ org_name: '', admin_username: '', admin_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/organizations`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to create organization.'); return; }
      onCreated();
    } catch { setError('Connection error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="sa-modal-backdrop" onClick={onClose}>
      <div className="sa-modal" onClick={e => e.stopPropagation()}>
        <div className="sa-modal-header">
          <h3>Create Organization</h3>
          <button className="sa-modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="sa-error">{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          {[
            { key: 'org_name',        label: 'Organization Name', type: 'text',     ph: 'Acme Property Group' },
            { key: 'admin_username',  label: 'Admin Username',    type: 'text',     ph: 'admin@acme.com' },
            { key: 'admin_password',  label: 'Admin Password',    type: 'password', ph: 'Min. 8 characters' },
          ].map(({ key, label, type, ph }) => (
            <div key={key} className="sa-field">
              <label>{label}</label>
              <input type={type} value={form[key]} placeholder={ph} required
                onChange={e => set(key, e.target.value)} />
            </div>
          ))}
          <div className="sa-modal-actions">
            <button type="button" className="sa-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="sa-btn-primary" disabled={loading}>
              {loading ? <span className="sa-spinner" /> : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Org Card ──────────────────────────────────────────────────────────────────

function OrgCard({ org, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async (e) => {
    e.stopPropagation();
    setToggling(true);
    try {
      const res = await fetch(`${API_BASE}/api/organizations/${org.org_id}/toggle`, {
        method: 'PATCH', headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) onToggle(org.org_id, data.is_active);
    } finally { setToggling(false); }
  };

  const stats = [
    { label: 'Admins',     value: org.admin_count },
    { label: 'Managers',   value: org.manager_count },
    { label: 'Properties', value: org.property_count },
    { label: 'Guidebooks', value: org.guidebook_count },
    { label: 'Sessions',   value: org.session_count },
  ];

  return (
    <div className={`sa-org-card ${org.is_active ? '' : 'sa-org-card--inactive'}`}>
      <div className="sa-org-header" onClick={() => setExpanded(v => !v)}>
        <div className="sa-org-icon"><BuildingIcon /></div>
        <div className="sa-org-info">
          <span className="sa-org-name">{org.org_name}</span>
          <span className="sa-org-id">ID: {org.org_id.slice(0, 8)}…</span>
        </div>
        <span className={`sa-badge ${org.is_active ? 'sa-badge--active' : 'sa-badge--inactive'}`}>
          {org.is_active ? 'Active' : 'Inactive'}
        </span>
        <button
          className={`sa-toggle-btn ${org.is_active ? 'sa-toggle-btn--deactivate' : 'sa-toggle-btn--activate'}`}
          onClick={handleToggle} disabled={toggling}
        >
          {toggling ? <span className="sa-spinner sa-spinner--sm" /> : (org.is_active ? 'Deactivate' : 'Activate')}
        </button>
        <div className="sa-expand-icon"><ChevronIcon open={expanded} /></div>
      </div>

      {/* Token usage row */}
      <div className="sa-token-row">
        <span className="sa-token-label">Tokens</span>
        <span className="sa-token-chip sa-token-chip--in" title="Input tokens">
          ↓ {fmtTokens(org.input_tokens)} in
        </span>
        <span className="sa-token-chip sa-token-chip--out" title="Output tokens">
          ↑ {fmtTokens(org.output_tokens)} out
        </span>
        <span className="sa-token-chip sa-token-chip--total">
          {fmtTokens(org.total_tokens)} total
        </span>
      </div>

      <div className="sa-org-stats">
        {stats.map(s => (
          <div key={s.label} className="sa-stat">
            <span className="sa-stat-val">{s.value}</span>
            <span className="sa-stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      {expanded && <OrgDetailsPanel orgId={org.org_id} orgName={org.org_name} />}
    </div>
  );
}

// ── Charts View ───────────────────────────────────────────────────────────────

function ChartsView() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/organizations/chart-data`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setChartData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="sa-loading">Loading charts…</div>;
  if (!chartData) return <div className="sa-empty">Failed to load chart data.</div>;

  return (
    <div className="sa-charts-grid">
      <div className="sa-chart-panel">
        <div className="sa-panel-title">Sessions — Last 14 Days (All Orgs)</div>
        <div className="sa-chart-body">
          <SessionsTrendChart data={chartData.sessions_by_day} />
        </div>
      </div>
      <div className="sa-chart-panel">
        <div className="sa-panel-title">Token Usage by Organization</div>
        <div className="sa-chart-body">
          <TokensByOrgChart data={chartData.tokens_by_org} />
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SuperAdminPage({ user, onLogout }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('orgs'); // 'orgs' | 'guidebooks' | 'properties' | 'charts'

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/organizations`, { headers: authHeaders() });
      if (res.ok) setOrgs(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleCreated = () => { setShowCreate(false); fetchOrgs(); };
  const handleToggle = (orgId, newStatus) => {
    setOrgs(prev => prev.map(o => o.org_id === orgId ? { ...o, is_active: newStatus } : o));
  };

  const filtered = orgs.filter(o =>
    o.org_name.toLowerCase().includes(search.toLowerCase()) ||
    o.org_id.toLowerCase().includes(search.toLowerCase())
  );

  const totalStats = {
    orgs:     orgs.length,
    active:   orgs.filter(o => o.is_active).length,
    managers: orgs.reduce((s, o) => s + o.manager_count, 0),
    sessions: orgs.reduce((s, o) => s + o.session_count, 0),
    tokens:   orgs.reduce((s, o) => s + o.total_tokens, 0),
  };

  return (
    <div className="sa-wrapper">
      <aside className="sa-sidebar">
        <div className="sa-logo"><BuildingIcon /><span>Super Admin</span></div>
        <nav className="sa-nav">
          <div className={`sa-nav-item ${view === 'orgs' ? 'sa-nav-item--active' : ''}`}
            onClick={() => setView('orgs')}>
            <BuildingIcon /> Organizations
          </div>
          <div className={`sa-nav-item ${view === 'guidebooks' ? 'sa-nav-item--active' : ''}`}
            onClick={() => setView('guidebooks')}>
            <BookIcon size={18} /> Guidebooks
          </div>
          <div className={`sa-nav-item ${view === 'properties' ? 'sa-nav-item--active' : ''}`}
            onClick={() => setView('properties')}>
            <HomeIcon size={18} /> Properties
          </div>
          <div className={`sa-nav-item ${view === 'charts' ? 'sa-nav-item--active' : ''}`}
            onClick={() => setView('charts')}>
            <ChartIcon /> Analytics
          </div>
        </nav>
        <button className="sa-logout" onClick={onLogout}>Sign Out</button>
      </aside>

      <main className="sa-main">
        {/* KPI strip — always visible */}
        <div className="sa-kpi-grid">
          {[
            { label: 'Total Orgs',     value: totalStats.orgs },
            { label: 'Active Orgs',    value: totalStats.active },
            { label: 'Total Managers', value: totalStats.managers },
            { label: 'Total Sessions', value: totalStats.sessions },
            { label: 'Total Tokens',   value: fmtTokens(totalStats.tokens) },
          ].map(k => (
            <div key={k.label} className="sa-kpi">
              <span className="sa-kpi-val">{k.value}</span>
              <span className="sa-kpi-lbl">{k.label}</span>
            </div>
          ))}
        </div>

        {/* Organizations view */}
        {view === 'orgs' && (
          <>
            <div className="sa-topbar">
              <div>
                <h1 className="sa-page-title">Organizations</h1>
                <p className="sa-page-sub">Manage all tenant organizations — click a row to see properties &amp; guidebooks</p>
              </div>
              <button className="sa-btn-primary sa-btn-create" onClick={() => setShowCreate(true)}>
                <PlusIcon /> New Organization
              </button>
            </div>

            <div className="sa-search-row">
              <input className="sa-search" placeholder="Search organizations…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>

            {loading ? (
              <div className="sa-loading">Loading organizations…</div>
            ) : filtered.length === 0 ? (
              search ? (
                <div className="sa-empty">No organizations match your search.</div>
              ) : (
                <div className="sa-empty-cta">
                  <div className="sa-empty-icon"><BuildingIcon /></div>
                  <h3>No organizations yet</h3>
                  <p>Create your first organization. Each org gets its own isolated admin, managers, properties and guidebooks.</p>
                  <button className="sa-btn-primary" onClick={() => setShowCreate(true)}>
                    <PlusIcon /> Create First Organization
                  </button>
                </div>
              )
            ) : (
              <div className="sa-org-list">
                {filtered.map(org => (
                  <OrgCard key={org.org_id} org={org} onToggle={handleToggle} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Guidebooks view */}
        {view === 'guidebooks' && <GuidebooksView />}

        {/* Properties view */}
        {view === 'properties' && <PropertiesView />}

        {/* Analytics view */}
        {view === 'charts' && (
          <>
            <div className="sa-topbar">
              <div>
                <h1 className="sa-page-title">Analytics</h1>
                <p className="sa-page-sub">Cross-organization sessions trend and token consumption</p>
              </div>
            </div>
            <ChartsView />
          </>
        )}
      </main>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
    </div>
  );
}
